/**
 * Assets Objects — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Assets objects: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129383223
 *
 * Content DRI: Belle Craig · Meter CD: Sarah Campbell
 *
 * NOTE ON PROFILE KEYS: The `UsageType` enum has stable keys
 * `exploring | adopting | scaling` for URL/state/analytics compatibility.
 * The Assets spec's visible labels are `Emerging | Adopting | Scaling`,
 * which are the customer-facing strings shown on the profile cards.
 * The keys are internal identifiers only — do NOT rename them.
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 */

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const ASSETS_OBJECTS_CONTENT: MeterContent = {
	description:
		'Use Assets to store and manage your objects and connect them to your work across Atlassian.',
	needsStep: {
		// Step 2 "Do you know your usage?" question — Assets objects copy.
		doYouKnowQuestion: 'Know how many objects you\u2019ll need?',
		// Assets spec: Step 2 primary CTA is "Next" (not the default
		// "Get estimate" used by other meters).
		nextCta: 'Next',
	},
	estimateStep: {
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Assets Objects you need, and how you can use them.',
		compareCta: 'Explore plans',
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per revised design direction (2026-07-16):
		// reframe Card 1 around ROI + built-in allowances with the
		// "Explore plans" CTA. Shared across all non-CSR meters.
		contentBlock: {
			title: 'Maximize your ROI with flexible, usage-based plans',
			intro:
				'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			listItems: ['Optimize your spend', 'Plan with confidence', 'Do more with less'],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — "Compare" per the latest design direction
		// (all meters share this comparison-focused verb on this step).
		nextCta: 'Compare',
	},
	summaryStep: {
		// Step 5 header — per Assets objects Confluence spec.
		title: 'Review usage estimate and compare',
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Assets Objects you need, and how you can use them.',
		orgNeedsCard: {
			eyebrow: 'YOUR ORGANIZATION NEEDS',
			headlineSubtext: 'Assets objects',
		},
		whatsCoveredCard: {
			eyebrow: 'WHAT\u2019S COVERED',
			headlineSubtext: 'Assets objects included monthly',
			bodyCovered: 'Your Assets objects are covered within the allowance.',
			bodyOverage:
				'There\u2019s a gap of {overage} Assets objects. You may incur extra usage charges of {cost}.',
			listItemsCovered: [
				'Higher plans provide more allowances',
				'Track your extra usage via your capabilities subscription details page in admin hub',
				'Set an extra usage limit to stay in budget',
			],
			listItemsOverage: [
				'Higher plans provide more allowances',
				'Track your extra usage via your capabilities subscription details page in admin hub',
				'Set an extra usage limit to stay in budget',
			],
		},
	},
	usageProfiles: {
		exploring: {
			label: 'Emerging',
			description:
				'Start tracking and managing the people and things that are important to your business.',
			usageRange: '0\u20133 objects per user',
			list: ['Track team members or equipment', 'Link objects to work'],
			valueProp:
				'Most teams start here and scale up as they connect more of their business to Assets.',
		},
		adopting: {
			label: 'Adopting',
			description:
				'Track a growing number of objects like people, places, and products. Good for teams tracking important relationships between objects.',
			usageRange: '4\u201310 objects per user',
			list: [
				'Map multiple structured object relationships',
				'Model business processes and org structures',
			],
			valueProp:
				'Teams at this usage level often manage many object schemas across multiple departments.',
		},
		scaling: {
			label: 'Scaling',
			description:
				'Track everything and anything that matters to your business, from laptops to llamas.',
			usageRange: '11\u201350 objects per user',
			list: [
				'Manage complex cross-team dependencies',
				'Add significant breadth and depth to the teamwork graph',
			],
			valueProp:
				'Organizations at this scale connect everything and anything that matters to their business.',
		},
	},
	valueProps: [
		{
			title: 'Track anything that matters',
			description:
				'Store and manage any type of business object, from equipment to contracts, locations to vendors, in one structured, searchable place.',
		},
		{
			title: 'Stay organized at scale',
			description:
				'Manage records and object relationships with room to grow as your team needs evolve.',
		},
		{
			title: 'Connect work across Atlassian',
			description: 'Link objects to your issues, requests, and pages, with full context, always.',
		},
	],
};
